// TaskDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Clock,
  Calendar,
  MessageSquare,
  Activity,
  Edit2,
  Trash2,
  Save,
  X,
  Send,
  AlertCircle,
  CheckCircle,
  Circle,
  Timer,
  Bell,
} from "lucide-react";
import apiService from "../../services/api";
import { toast } from "react-hot-toast";
import { useTheme } from "../../contexts/ThemeContext";

const TaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [newComment, setNewComment] = useState("");
  const [reminderLoading, setReminderLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "todo",
    priority: "medium",
    dueDate: "",
    startDate: "",
    estimatedHours: "",
    actualHours: "",
    progress: 0,
  });

  useEffect(() => {
    loadTask();
  }, [id]);

  const loadTask = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTask(id);
      setTask(response.data);
      populateForm(response.data);
    } catch (error) {
      console.error("Error loading task:", error);
      toast.error("Failed to load task");
    } finally {
      setLoading(false);
    }
  };

  const toDateInputValue = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
  };

  const populateForm = (data) => {
    setFormData({
      title: data.title || "",
      description: data.description || "",
      status: data.status || "todo",
      priority: data.priority || "medium",
      dueDate: toDateInputValue(data.dueDate),
      startDate: toDateInputValue(data.startDate),
      estimatedHours: data.estimatedHours || "",
      actualHours: data.actualHours || "",
      progress: data.progress || 0,
    });
  };

  const sanitisePayload = (data) => ({
    ...data,
    dueDate: data.dueDate || null,
    startDate: data.startDate || null,
    estimatedHours:
      data.estimatedHours !== "" ? Number(data.estimatedHours) : null,
    actualHours: data.actualHours !== "" ? Number(data.actualHours) : null,
  });

  const handleUpdate = async () => {
    try {
      await apiService.updateTask(id, sanitisePayload(formData));
      toast.success("Task updated");
      setIsEditing(false);
      loadTask();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      await apiService.deleteTask(id);
      toast.success("Task deleted");
      navigate("/tasks");
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await apiService.addComment(id, { content: newComment });
      toast.success("Comment added");
      setNewComment("");
      loadTask();
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    }
  };

  const handleToggleReminder = async () => {
    if (!task.dueDate) {
      toast.error("A due date is required to enable reminders");
      return;
    }
    const newValue = !task.reminderEnabled;
    setReminderLoading(true);
    try {
      await apiService.updateTask(id, { reminderEnabled: newValue });
      setTask((prev) => ({ ...prev, reminderEnabled: newValue }));
      toast.success(newValue ? "Reminder enabled" : "Reminder disabled");
    } catch (error) {
      console.error("Error toggling reminder:", error);
      toast.error("Failed to update reminder");
    } finally {
      setReminderLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "Not set";
    return new Date(date).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "Not set";
    return new Date(date).toLocaleString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      todo: "bg-gray-600 text-gray-200 border-gray-500",
      in_progress: "bg-blue-600 text-white border-blue-500",
      review: "bg-yellow-600 text-white border-yellow-500",
      completed: "bg-green-600 text-white border-green-500",
      blocked: "bg-red-600 text-white border-red-500",
    };
    return colors[status] || colors.todo;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "text-gray-400 bg-gray-400/10",
      medium: "text-yellow-400 bg-yellow-400/10",
      high: "text-orange-400 bg-orange-400/10",
      urgent: "text-red-400 bg-red-400/10",
    };
    return colors[priority] || colors.medium;
  };

  const getStatusIcon = (status) => {
    const icons = {
      todo: Circle,
      in_progress: Clock,
      review: AlertCircle,
      completed: CheckCircle,
      blocked: X,
    };
    const Icon = icons[status] || Circle;
    return <Icon size={18} />;
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDarkMode ? "bg-[#0A0B0D]" : "bg-[#F4F5F7]"
        }`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Loading task...
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          isDarkMode ? "bg-[#0A0B0D]" : "bg-[#F4F5F7]"
        }`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Task not found
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 p-6 ${
        isDarkMode ? "bg-[#0A0B0D] text-white" : "bg-[#F4F5F7] text-gray-900"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center gap-2 mb-4 transition-colors ${
              isDarkMode
                ? "text-[#94979C] hover:text-white"
                : "text-gray-500 hover:text-gray-900"
            }`}
          >
            <ArrowLeft size={20} />
            <span>Back to Tasks</span>
          </button>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              {isEditing ? (
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className={`text-3xl font-bold rounded-lg px-4 py-2 w-full mb-2 focus:outline-none ${
                    isDarkMode
                      ? "bg-[#13161B] border border-gray-700 text-white focus:border-[#427BCA]"
                      : "bg-white border border-gray-300 text-gray-900 focus:border-blue-400"
                  }`}
                />
              ) : (
                <h1
                  className={`text-3xl font-bold mb-2 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {task.title}
                </h1>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <span
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border ${getStatusColor(
                    isEditing ? formData.status : task.status,
                  )}`}
                >
                  {getStatusIcon(isEditing ? formData.status : task.status)}
                  {isEditing ? (
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({ ...formData, status: e.target.value })
                      }
                      className="bg-transparent border-none focus:outline-none cursor-pointer"
                    >
                      <option value="todo">To Do</option>
                      <option value="in_progress">In Progress</option>
                      <option value="review">Review</option>
                      <option value="completed">Completed</option>
                      <option value="blocked">Blocked</option>
                    </select>
                  ) : (
                    task.status.replace("_", " ").toUpperCase()
                  )}
                </span>

                <span
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${getPriorityColor(
                    isEditing ? formData.priority : task.priority,
                  )}`}
                >
                  {isEditing ? (
                    <select
                      value={formData.priority}
                      onChange={(e) =>
                        setFormData({ ...formData, priority: e.target.value })
                      }
                      className="bg-transparent border-none focus:outline-none cursor-pointer"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  ) : (
                    `${task.priority.toUpperCase()} PRIORITY`
                  )}
                </span>

                {task.dueDate && (
                  <span
                    className={`text-sm flex items-center gap-2 ${
                      isDarkMode ? "text-[#94979C]" : "text-gray-500"
                    }`}
                  >
                    <Calendar size={16} />
                    Due: {formatDate(task.dueDate)}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      populateForm(task);
                    }}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                      isDarkMode
                        ? "bg-[#13161B] border border-gray-700 hover:bg-gray-700"
                        : "bg-gray-100 border border-gray-300 hover:bg-gray-200 text-gray-700"
                    }`}
                  >
                    <X size={18} />
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdate}
                    className="px-4 py-2 bg-[#427BCA] rounded-lg hover:bg-[#5A9BE8] transition-colors flex items-center gap-2 text-white"
                  >
                    <Save size={18} />
                    Save
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                      isDarkMode
                        ? "bg-[#13161B] border border-gray-700 hover:bg-gray-700"
                        : "bg-gray-100 border border-gray-300 hover:bg-gray-200 text-gray-700"
                    }`}
                  >
                    <Edit2 size={18} />
                    Edit
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600/10 border border-red-600/30 rounded-lg hover:bg-red-600/20 transition-colors flex items-center gap-2 text-red-400"
                  >
                    <Trash2 size={18} />
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div
          className={`border-b mb-6 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex gap-6">
            {["details", "comments", "activity"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 px-2 border-b-2 transition-colors capitalize ${
                  activeTab === tab
                    ? "border-[#427BCA] text-[#427BCA]"
                    : isDarkMode
                      ? "border-transparent text-[#94979C] hover:text-white"
                      : "border-transparent text-gray-500 hover:text-gray-900"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Main content */}
          <div className="col-span-2 space-y-6">
            {/* ── Details tab ─────────────────────────────────────────── */}
            {activeTab === "details" && (
              <div
                className={`rounded-xl border p-6 transition-colors ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700/50"
                    : "bg-white border-gray-200 shadow-sm"
                }`}
              >
                <h2
                  className={`text-xl font-semibold mb-4 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Description
                </h2>
                {isEditing ? (
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={8}
                    className={`w-full px-4 py-3 rounded-lg focus:outline-none ${
                      isDarkMode
                        ? "bg-[#13161B] border border-gray-700 text-white focus:border-[#427BCA]"
                        : "bg-gray-50 border border-gray-300 text-gray-900 focus:border-blue-400"
                    }`}
                    placeholder="Add a description..."
                  />
                ) : (
                  <p
                    className={`whitespace-pre-wrap ${
                      isDarkMode ? "text-[#94979C]" : "text-gray-600"
                    }`}
                  >
                    {task.description || "No description provided."}
                  </p>
                )}

                {/* Progress */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <label
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Progress
                    </label>
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {isEditing ? formData.progress : task.progress}%
                    </span>
                  </div>
                  {isEditing ? (
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.progress}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          progress: parseInt(e.target.value),
                        })
                      }
                      className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#427BCA]"
                    />
                  ) : (
                    <div
                      className={`w-full rounded-full h-2 ${
                        isDarkMode ? "bg-gray-700" : "bg-gray-200"
                      }`}
                    >
                      <div
                        className="bg-[#427BCA] h-2 rounded-full transition-all"
                        style={{ width: `${task.progress}%` }}
                      />
                    </div>
                  )}
                </div>

                {/* Time tracking */}
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Estimated Hours
                    </label>
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.5"
                        value={formData.estimatedHours}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            estimatedHours: e.target.value,
                          })
                        }
                        className={`w-full px-4 py-2 rounded-lg focus:outline-none ${
                          isDarkMode
                            ? "bg-[#13161B] border border-gray-700 text-white focus:border-[#427BCA]"
                            : "bg-gray-50 border border-gray-300 text-gray-900"
                        }`}
                      />
                    ) : (
                      <div
                        className={`flex items-center gap-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        <Timer size={18} className="text-[#427BCA]" />
                        {task.estimatedHours || "0"} hours
                      </div>
                    )}
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Actual Hours
                    </label>
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.5"
                        value={formData.actualHours}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            actualHours: e.target.value,
                          })
                        }
                        className={`w-full px-4 py-2 rounded-lg focus:outline-none ${
                          isDarkMode
                            ? "bg-[#13161B] border border-gray-700 text-white focus:border-[#427BCA]"
                            : "bg-gray-50 border border-gray-300 text-gray-900"
                        }`}
                      />
                    ) : (
                      <div
                        className={`flex items-center gap-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        <Clock size={18} className="text-[#427BCA]" />
                        {task.actualHours || "0"} hours
                      </div>
                    )}
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Start Date
                    </label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            startDate: e.target.value,
                          })
                        }
                        className={`w-full px-4 py-2 rounded-lg focus:outline-none ${
                          isDarkMode
                            ? "bg-[#13161B] border border-gray-700 text-white focus:border-[#427BCA]"
                            : "bg-gray-50 border border-gray-300 text-gray-900"
                        }`}
                      />
                    ) : (
                      <div
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {formatDate(task.startDate)}
                      </div>
                    )}
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Due Date
                    </label>
                    {isEditing ? (
                      <input
                        type="date"
                        value={formData.dueDate}
                        onChange={(e) =>
                          setFormData({ ...formData, dueDate: e.target.value })
                        }
                        className={`w-full px-4 py-2 rounded-lg focus:outline-none ${
                          isDarkMode
                            ? "bg-[#13161B] border border-gray-700 text-white focus:border-[#427BCA]"
                            : "bg-gray-50 border border-gray-300 text-gray-900"
                        }`}
                      />
                    ) : (
                      <div
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {formatDate(task.dueDate)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Comments tab ─────────────────────────────────────────── */}
            {activeTab === "comments" && (
              <div
                className={`rounded-xl border p-6 transition-colors ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700/50"
                    : "bg-white border-gray-200 shadow-sm"
                }`}
              >
                <h2
                  className={`text-xl font-semibold mb-4 flex items-center gap-2 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  <MessageSquare size={24} />
                  Comments ({task.comments?.length || 0})
                </h2>

                <div className="mb-6">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                    className={`w-full px-4 py-3 rounded-lg focus:outline-none mb-3 ${
                      isDarkMode
                        ? "bg-[#13161B] border border-gray-700 text-white focus:border-[#427BCA]"
                        : "bg-gray-50 border border-gray-300 text-gray-900 focus:border-blue-400"
                    }`}
                  />
                  <div className="flex justify-end">
                    <button
                      onClick={handleAddComment}
                      disabled={!newComment.trim()}
                      className="px-4 py-2 bg-[#427BCA] rounded-lg hover:bg-[#5A9BE8] transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-white"
                    >
                      <Send size={18} />
                      Post Comment
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {task.comments && task.comments.length > 0 ? (
                    task.comments.map((comment) => (
                      <div
                        key={comment.id}
                        className={`rounded-lg p-4 border ${
                          isDarkMode
                            ? "bg-[#13161B] border-gray-700/50"
                            : "bg-gray-50 border-gray-100"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {comment.user?.profileImageUrl ? (
                            <img
                              src={comment.user.profileImageUrl}
                              alt={comment.user.name}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                                isDarkMode
                                  ? "bg-[#427BCA]/20 border border-[#427BCA]/30 text-[#427BCA]"
                                  : "bg-blue-100 border border-blue-200 text-blue-600"
                              }`}
                            >
                              {comment.user?.name
                                ?.split(" ")
                                .map((n) => n[0])
                                .join("")
                                .toUpperCase()
                                .substring(0, 2) || "?"}
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={`font-medium ${
                                  isDarkMode ? "text-white" : "text-gray-900"
                                }`}
                              >
                                {comment.user?.name || "Unknown"}
                              </span>
                              <span
                                className={`text-xs ${
                                  isDarkMode
                                    ? "text-[#94979C]"
                                    : "text-gray-500"
                                }`}
                              >
                                {formatDateTime(comment.createdAt)}
                              </span>
                            </div>
                            <p
                              className={`whitespace-pre-wrap ${
                                isDarkMode ? "text-[#94979C]" : "text-gray-600"
                              }`}
                            >
                              {comment.content}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div
                      className={`text-center py-8 ${
                        isDarkMode ? "text-[#94979C]" : "text-gray-500"
                      }`}
                    >
                      No comments yet.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Activity tab ─────────────────────────────────────────── */}
            {activeTab === "activity" && (
              <div
                className={`rounded-xl border p-6 transition-colors ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700/50"
                    : "bg-white border-gray-200 shadow-sm"
                }`}
              >
                <h2
                  className={`text-xl font-semibold mb-4 flex items-center gap-2 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  <Activity size={24} />
                  Activity Timeline
                </h2>

                <div className="space-y-4">
                  {task.activities && task.activities.length > 0 ? (
                    task.activities.map((activity, index) => (
                      <div
                        key={activity.id}
                        className="flex items-start gap-4 relative"
                      >
                        {index !== task.activities.length - 1 && (
                          <div
                            className={`absolute left-5 top-10 bottom-0 w-px ${
                              isDarkMode ? "bg-gray-700" : "bg-gray-200"
                            }`}
                          />
                        )}
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 ${
                            isDarkMode
                              ? "bg-[#427BCA]/20 border border-[#427BCA]/30"
                              : "bg-blue-100 border border-blue-200"
                          }`}
                        >
                          <Activity size={18} className="text-[#427BCA]" />
                        </div>
                        <div className="flex-1 pt-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className={`font-medium ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {activity.user?.name || "System"}
                            </span>
                            <span
                              className={`text-sm ${
                                isDarkMode ? "text-[#94979C]" : "text-gray-500"
                              }`}
                            >
                              {activity.action}
                            </span>
                          </div>
                          <div
                            className={`text-xs ${
                              isDarkMode ? "text-[#94979C]" : "text-gray-500"
                            }`}
                          >
                            {formatDateTime(activity.createdAt)}
                          </div>
                          {activity.changes && (
                            <div
                              className={`mt-2 text-sm rounded p-2 border ${
                                isDarkMode
                                  ? "text-[#94979C] bg-[#13161B] border-gray-700/50"
                                  : "text-gray-600 bg-gray-50 border-gray-200"
                              }`}
                            >
                              <pre className="text-xs">
                                {JSON.stringify(activity.changes, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div
                      className={`text-center py-8 ${
                        isDarkMode ? "text-[#94979C]" : "text-gray-500"
                      }`}
                    >
                      No activity recorded yet.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Sidebar ──────────────────────────────────────────────────── */}
          <div className="space-y-6">
            {/* Email Reminder */}
            <div
              className={`rounded-xl border p-6 transition-colors ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700/50"
                  : "bg-white border-gray-200 shadow-sm"
              }`}
            >
              <h3
                className={`text-sm font-medium mb-4 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Email Reminder
              </h3>

              <button
                onClick={handleToggleReminder}
                disabled={reminderLoading || !task.dueDate}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-colors ${
                  !task.dueDate
                    ? "opacity-40 cursor-not-allowed border-gray-700 bg-transparent"
                    : task.reminderEnabled
                      ? "border-blue-500 bg-blue-500/10 cursor-pointer"
                      : isDarkMode
                        ? "border-gray-700 bg-transparent hover:border-gray-500 cursor-pointer"
                        : "border-gray-300 bg-transparent hover:border-gray-400 cursor-pointer"
                }`}
              >
                <Bell
                  className={`w-4 h-4 flex-shrink-0 ${
                    task.reminderEnabled
                      ? "text-blue-400"
                      : isDarkMode
                        ? "text-gray-400"
                        : "text-gray-500"
                  }`}
                />
                <div className="text-left flex-1">
                  <p
                    className={`text-sm font-medium leading-tight ${
                      task.reminderEnabled
                        ? "text-blue-400"
                        : isDarkMode
                          ? "text-gray-300"
                          : "text-gray-700"
                    }`}
                  >
                    {reminderLoading
                      ? "Updating..."
                      : task.reminderEnabled
                        ? "Reminder On"
                        : "Reminder Off"}
                  </p>
                  <p
                    className={`text-xs leading-tight mt-0.5 ${
                      isDarkMode ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    {!task.dueDate
                      ? "Set a due date first"
                      : task.reminderEnabled
                        ? "Day before & on the day"
                        : "Click to enable"}
                  </p>
                </div>

                {/* Toggle pill */}
                <div className="flex-shrink-0">
                  <div
                    className={`w-9 h-5 rounded-full transition-colors relative ${
                      task.reminderEnabled
                        ? "bg-blue-500"
                        : isDarkMode
                          ? "bg-gray-600"
                          : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                        task.reminderEnabled
                          ? "translate-x-4"
                          : "translate-x-0.5"
                      }`}
                    />
                  </div>
                </div>
              </button>
            </div>

            {/* Assignee */}
            <div
              className={`rounded-xl border p-6 transition-colors ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700/50"
                  : "bg-white border-gray-200 shadow-sm"
              }`}
            >
              <h3
                className={`text-sm font-medium mb-3 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Assigned To
              </h3>
              <div className="flex items-center gap-3">
                {task.assignedUser?.profileImageUrl ? (
                  <img
                    src={task.assignedUser.profileImageUrl}
                    alt={task.assignedUser.name}
                    className="w-12 h-12 rounded-full"
                  />
                ) : (
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold ${
                      isDarkMode
                        ? "bg-[#427BCA]/20 border border-[#427BCA]/30 text-[#427BCA]"
                        : "bg-blue-100 border border-blue-200 text-blue-600"
                    }`}
                  >
                    {task.assignedUser?.name
                      ?.split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .substring(0, 2) || "?"}
                  </div>
                )}
                <div>
                  <div
                    className={`font-medium ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {task.assignedUser?.name || "Unassigned"}
                  </div>
                  <div
                    className={`text-xs ${
                      isDarkMode ? "text-[#94979C]" : "text-gray-500"
                    }`}
                  >
                    {task.assignedUser?.email || ""}
                  </div>
                </div>
              </div>
            </div>

            {/* Creator */}
            <div
              className={`rounded-xl border p-6 transition-colors ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700/50"
                  : "bg-white border-gray-200 shadow-sm"
              }`}
            >
              <h3
                className={`text-sm font-medium mb-3 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Created By
              </h3>
              <div className="flex items-center gap-3">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold ${
                    isDarkMode
                      ? "bg-[#427BCA]/20 border border-[#427BCA]/30 text-[#427BCA]"
                      : "bg-blue-100 border border-blue-200 text-blue-600"
                  }`}
                >
                  {task.creator?.name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .substring(0, 2) || "?"}
                </div>
                <div>
                  <div
                    className={`font-medium ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {task.creator?.name || "Unknown"}
                  </div>
                  <div
                    className={`text-xs ${
                      isDarkMode ? "text-[#94979C]" : "text-gray-500"
                    }`}
                  >
                    {task.creator?.email || ""}
                  </div>
                </div>
              </div>
            </div>

            {/* Task metadata */}
            <div
              className={`rounded-xl border p-6 transition-colors ${
                isDarkMode
                  ? "bg-[#0C0E12] border-gray-700/50"
                  : "bg-white border-gray-200 shadow-sm"
              }`}
            >
              <h3
                className={`text-sm font-medium mb-4 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Task Details
              </h3>
              <div className="space-y-3">
                <div>
                  <div
                    className={`text-xs mb-1 ${
                      isDarkMode ? "text-[#94979C]" : "text-gray-500"
                    }`}
                  >
                    Created
                  </div>
                  <div
                    className={`text-sm ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {formatDateTime(
                      task.salesforceCreatedDate || task.createdAt,
                    )}
                  </div>
                </div>
                <div>
                  <div
                    className={`text-xs mb-1 ${
                      isDarkMode ? "text-[#94979C]" : "text-gray-500"
                    }`}
                  >
                    Last Updated
                  </div>
                  <div
                    className={`text-sm ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {formatDateTime(
                      task.salesforceLastModifiedDate || task.updatedAt,
                    )}
                  </div>
                </div>
                {task.completedAt && (
                  <div>
                    <div
                      className={`text-xs mb-1 ${
                        isDarkMode ? "text-[#94979C]" : "text-gray-500"
                      }`}
                    >
                      Completed
                    </div>
                    <div
                      className={`text-sm ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {formatDateTime(task.completedAt)}
                    </div>
                  </div>
                )}
                {task.team && (
                  <div>
                    <div
                      className={`text-xs mb-1 ${
                        isDarkMode ? "text-[#94979C]" : "text-gray-500"
                      }`}
                    >
                      Team
                    </div>
                    <div
                      className={`text-sm ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {task.team.name}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Related to */}
            {(task.leadId ||
              task.opportunityId ||
              task.accountId ||
              task.contactId ||
              task.caseId) && (
              <div
                className={`rounded-xl border p-6 transition-colors ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700/50"
                    : "bg-white border-gray-200 shadow-sm"
                }`}
              >
                <h3
                  className={`text-sm font-medium mb-4 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Related To
                </h3>
                <div className="space-y-2">
                  {task.opportunityId && (
                    <div className="flex items-center gap-2 text-sm">
                      <span
                        className={`shrink-0 ${
                          isDarkMode ? "text-[#94979C]" : "text-gray-500"
                        }`}
                      >
                        Opportunity:
                      </span>
                      <button
                        onClick={() =>
                          navigate(`/opportunities/${task.opportunityId}`)
                        }
                        className="text-[#427BCA] hover:underline text-left truncate"
                      >
                        {task.opportunity?.name || "View Opportunity"}
                      </button>
                    </div>
                  )}
                  {task.accountId && (
                    <div className="flex items-center gap-2 text-sm">
                      <span
                        className={`shrink-0 ${
                          isDarkMode ? "text-[#94979C]" : "text-gray-500"
                        }`}
                      >
                        Account:
                      </span>
                      <button
                        onClick={() => navigate(`/accounts/${task.accountId}`)}
                        className="text-[#427BCA] hover:underline text-left truncate"
                      >
                        {task.account?.name || "View Account"}
                      </button>
                    </div>
                  )}
                  {task.leadId && (
                    <div className="flex items-center gap-2 text-sm">
                      <span
                        className={`shrink-0 ${
                          isDarkMode ? "text-[#94979C]" : "text-gray-500"
                        }`}
                      >
                        Lead:
                      </span>
                      <button
                        onClick={() => navigate(`/leads/${task.leadId}`)}
                        className="text-[#427BCA] hover:underline text-left truncate"
                      >
                        {task.lead?.firstName && task.lead?.lastName
                          ? `${task.lead.firstName} ${task.lead.lastName}`
                          : task.lead?.name || "View Lead"}
                      </button>
                    </div>
                  )}
                  {task.contactId && (
                    <div className="flex items-center gap-2 text-sm">
                      <span
                        className={`shrink-0 ${
                          isDarkMode ? "text-[#94979C]" : "text-gray-500"
                        }`}
                      >
                        Contact:
                      </span>
                      <button
                        onClick={() => navigate(`/contacts/${task.contactId}`)}
                        className="text-[#427BCA] hover:underline text-left truncate"
                      >
                        {task.contact?.firstName && task.contact?.lastName
                          ? `${task.contact.firstName} ${task.contact.lastName}`
                          : task.contact?.name || "View Contact"}
                      </button>
                    </div>
                  )}
                  {task.caseId && (
                    <div className="flex items-center gap-2 text-sm">
                      <span
                        className={`shrink-0 ${
                          isDarkMode ? "text-[#94979C]" : "text-gray-500"
                        }`}
                      >
                        Case:
                      </span>
                      <button
                        onClick={() => navigate(`/cases/${task.caseId}`)}
                        className="text-[#427BCA] hover:underline text-left truncate"
                      >
                        {task.case?.subject ||
                          (task.case?.caseNumber
                            ? `Case #${task.case.caseNumber}`
                            : "View Case")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetail;
